import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, ViewChildren, QueryList } from '@angular/core';
import {
    Observable,
    catchError,
    first,
    of,
    Subscription,
    Subject,
    switchMap,
    map,
    BehaviorSubject,
    tap,
    filter,
    forkJoin,
    take,
    takeUntil,
    timeout,
    TimeoutError
} from 'rxjs';
import { APP_BASE_HREF } from '@angular/common';
import { isNil } from 'lodash';
import { MessageService } from 'primeng/api';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { BriefingsService } from '../../services/briefings.service';
import { deliverySchedulingTypesEnum } from '../../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { BriefingModel } from '../../models/briefing.model';
import { BriefingRecurrencePatternModel } from '../../models/briefing-recurrence-pattern.model';
import { MonitoringSeriesTypeEnum } from '../../../Shared/enums/monitoring-series-type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { OnlineBriefingBannerStyleEnum } from '../../enums/online-briefing-banner-style.enum';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { TemplateCardModel } from '../../../../Common/TargetingShared/model/template-card.model';
import { OnlineBriefingTemplateEnum } from '../../enums/online-briefing-template.enum';
import { BriefingListItemModel } from '../../../../Common/MonitorShared/model/briefing-list-item.model';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { BriefingTemplateService } from '../../../../Common/MonitorShared/services/briefing-template.service';
import { BriefingTypeEnum } from '../../enums/briefing-type.enum';
import { BriefingStatusEnum } from '../../../../Common/MonitorShared/enum/briefing-status.enum';
import { ManageTemplateDialogComponent } from '../../../../Common/MonitorShared/components/ManageTemplateDialog/manage-template-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { OnlineBriefingTemplateConfigModel } from '../../models/online-briefing-option.model';
import { BriefingsTemplateFactorService } from '../../services/briefings-template-factory.service';
import { BriefingCoverageAnalysisService } from '../../services/briefing-coverage-analysis.service';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { CuratorBlock } from '../../../../Common/PageBuilder/models/curator-block.model';
import { LayoutBlock } from '../../../../Common/PageBuilder/models/layout-block.model';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import { MentionSearchSettingService } from '@Common/MonitorShared/services/mention-search-setting.service';
import { QuarterlyDateRangeSettingsModel, QuarterOptionEnum } from '../../../Shared/models/quarterly-date-range-settings.model';
import { CanvasAgentTemplateEnum } from '@api/ai/enums/canvas-agent-template.enum';
import {
    NewCanvasAgentResponseApiModel,
    NewCanvasAgentCoverageDateRangeApiModel,
    NewCanvasAgentSourceBlockModel,
    CanvasV2ProgressEvent
} from '@api/ai/model/new-canvas-agent-response-api.model';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchModel } from '../../../Shared/models/mention-search.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';
import { BriefingNaturalLanguageLoadingDialogComponent } from '../BriefingNaturalLanguageLoadingDialog/briefing-natural-language-loading-dialog.component';
import { CanvasProgressState, CanvasProgressStep, CanvasProgressTopic } from '@Monitor/Briefings/models/canvas-v2-progress.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSearchCriteriaModel } from '../../../Shared/models/mention-search-criteria.model';
import { DeliveryRecurrenceFrequencyEnum } from '@Common/Forms/enums/delivery-recurrence-frequency.enum';
import { TimeModel } from '@Common/Forms/models/time.model';
import { TwentyFourHourModel } from '@Common/Forms/models/twenty-four-hour.model';
import { MinuteModel } from '@Common/Forms/models/minute.model';
import { meridiansEnum } from '@Common/Forms/enums/meridians.enum';
import { timeFormatsEnum } from '@Core/Users/enums/time-formats.enum';
import { CanvasAgentResponseApiModelRecurrenceTimingIntervalEnum } from '@api/ai/enums/canvas-agent-response-recurrence-timing-interval.enum';
import { CanvasAgentResponseApiModelSchedulingTypeEnum } from '@api/ai/enums/canvas-agent-response-recurrence-scheduling-type.enum';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';
import { AnalysisDisplayElementModel } from '@Monitor/Briefings/models/briefing-analysis-display-element.model';
import { CanvasV2EventTypeEnum } from '../../enums/canvas-v2-event-type.enum';

import { AIContextTakeoverComponent } from '../../../../Common/Dialogs/components/AIContextTakeover/ai-context-takeover.component';
import { canvasAgentSystemPrompt } from '../../../../api/agents/models/canvas-agent-system-prompt.api.model';
import { MonitoringBackEndDataSourceModel } from '../../../Shared/models/monitoring-back-end-data-source.model';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MonitoringBrandManagementService } from '../../../Shared/services/monitoring-brand-management.service';
import { MonitoringBrandCardComponent, MonthlyConfigs, QuarterlyConfigs } from '../../../Shared/components/MonitoringBrandCard/monitoring-brand-card.component';
import { MonitoringBrand } from '../../../Shared/models/monitoring-brand.model';
import { MonitoringSourcePickerFieldOptions } from '../../../Shared/models/monitoring-source-picker-field.model';
import { MonthlyDateRangeSettingsModel, MonthOptionEnum } from '@Monitor/Shared/models/monthly-date-range-settings.model';
import moment from 'moment';

enum NewOnlineBriefingTabsEnum {
    quickTemplates,
    savedTemplates,
    pastBriefings,
    naturalLanguage
}

@Component({
    selector: 'agpr-new-online-briefing',
    templateUrl: './new-online-briefing.component.html',
    styleUrls: ['./new-online-briefing.component.scss'],
    standalone: false,
    providers: [
        MentionSearchSettingService,
        MonitoringBrandManagementService
    ]
})
export class NewOnlineBriefingComponent implements OnInit, OnDestroy {
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly deliverySchedulingTypes = deliverySchedulingTypesEnum;
    readonly monitoringSeriesTypes = MonitoringSeriesTypeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly onlineBriefingBannerStyle = OnlineBriefingBannerStyleEnum;
    readonly newOnlineBriefingTabsEnum = NewOnlineBriefingTabsEnum;
    readonly onlineBriefingTemplateEnum = OnlineBriefingTemplateEnum;
    readonly weekdayMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    readonly defaultColor = '#0D74BA';

    private readonly destroy$ = new Subject<void>();

    private readonly TOOL_ACTIVITY_LABELS: Record<string, string> = {
        search_mentions: 'Briefings.newBriefing.naturalLanguage.loadingPanel.topic.searchingMentions',
        search_geographies: 'Briefings.newBriefing.naturalLanguage.loadingPanel.topic.searchingGeographies',
        web_search: 'Briefings.newBriefing.naturalLanguage.loadingPanel.topic.searchingWeb',
        url_loader: 'Briefings.newBriefing.naturalLanguage.loadingPanel.topic.readingSource'
    };

    @ViewChildren(MonitoringBrandCardComponent) allBrandCards: QueryList<MonitoringBrandCardComponent> = new QueryList();
    @ViewChildren(MonitoringBrandCardComponent, {read: ElementRef}) allBrandCardRefs: QueryList<ElementRef> = new QueryList();

    @ViewChild('rightScrollPanel') rightScrollPanel: ScrollPanelComponent;
    @ViewChild('leftScrollPanel') leftScrollPanel: ScrollPanelComponent;
    @ViewChild('brandCardTemp') brandCardTemp: MonitoringBrandCardComponent;
    @ViewChild('brandCardDataSourceLeft') brandCardDataSourceLeft: ElementRef | undefined;
    @ViewChild('brandCardDataSourceRight') brandCardDataSourceRight: ElementRef | undefined;

    briefing: BriefingModel = new BriefingModel();
    isChoosingTemplate: boolean = true;
    hasOpenAI: boolean = false;
    hasAgent: boolean = false;
    hasAgentDebug: boolean = false;
    enableCoPilot = false;
    timezonePreference: string = null;
    timeFormatPreference: timeFormatsEnum = null;

    selectedTabIndex: NewOnlineBriefingTabsEnum = NewOnlineBriefingTabsEnum.quickTemplates;
    // The first option is always selected as default
    onlineBriefingTemplates: TemplateCardModel[] = [
        {
            id: OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_DEFAULT,
            title: 'Briefing.onlineBriefing.template.default',
            icon: 'fa-duotone fa-light fa-newspaper',
            description: 'Briefing.onlineBriefing.template.default.description',
            selected: true
        },
        {
            id: OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR,
            icon: 'fa-duotone fa-light fa-building-magnifying-glass',
            title: 'Briefing.onlineBriefing.template.competitor',
            description: 'Briefing.onlineBriefing.template.competitor.description',
        },
        {
            id: OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY,
            title: 'Briefing.onlineBriefing.template.monthly',
            description: 'Briefing.onlineBriefing.template.monthly.description',
            icon: 'fa-duotone fa-regular fa-calendar',
        },
        {
            id: OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY,
            title: 'Briefing.onlineBriefing.template.quarterly',
            description: 'Briefing.onlineBriefing.template.quarterly.description',
            icon: 'fa-duotone fa-regular fa-circle-quarter-stroke',
        },
        {
            id: OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_BLANK,
            title: 'Briefing.onlineBriefing.template.blank',
            icon: 'fa-duotone fa-light fa-file',
            description: 'Briefing.onlineBriefing.template.blank.description'
        },
    ];
    selectedTemplate: OnlineBriefingTemplateEnum = OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_DEFAULT;

    briefingListItems: BriefingListItemModel[];
    selectedBriefing: BriefingListItemModel = null;

    canvasTemplateListItems: BriefingListItemModel[] = [];
    selectedCanvasTemplate: BriefingListItemModel | null = null;

    selectedSchedulingType: deliverySchedulingTypesEnum = deliverySchedulingTypesEnum.immediate;

    analysisFocus: string = '';
    // The analysis focus is reused as a section title persisted to the BriefingCuratedMentions.title
    // VARCHAR(255) column after the quarterly template decorates it with prefixes/suffixes, so cap it
    // below 255 with headroom — enforced both on this manual input and on the AI-generated value.
    readonly analysisFocusMaxLength = 180;
    selectedBannerStyle: OnlineBriefingBannerStyleEnum = OnlineBriefingBannerStyleEnum.SIMPLE;
    subscriptions: Subscription[] = [];
    dateFormat: string;
    naturalLanguagePrompt: string = '';
    taskDescription: string = '';
    systemPrompt: string = '';
    topicSystemPrompt: string = '';
    aiIsGenerating: boolean = false;
    /**
     * How long the Canvas agent may go without sending a progress event before we give up.
     * The websocket has no replay, so a dropped connection or a failed push on the agent's side
     * would otherwise leave the progress dialog spinning forever.
     */
    private readonly canvasGenerationIdleTimeoutMs = 4 * 60 * 1000;

    defaultColors: string[] = [];
    brandCardDataSourceConfigs: MonitoringSourcePickerFieldOptions;
    competitorCardDataSourceConfigs: MonitoringSourcePickerFieldOptions;
    quarterlySettings: QuarterlyDateRangeSettingsModel = new QuarterlyDateRangeSettingsModel();
    quarterlyConfigs: QuarterlyConfigs;
    monthlySettings: MonthlyDateRangeSettingsModel = new MonthlyDateRangeSettingsModel();
    monthlyConfigs: MonthlyConfigs;

    /** Recurring delivery only supports the most-recent quarter; drives the picker's quarterly option list. */
    get restrictToMostRecentQuarter(): boolean {
        return this.selectedSchedulingType === deliverySchedulingTypesEnum.recurring;
    }
    brandCard: MonitoringBrand = {
        brand: null,
        name: this.localizationService.transform('Briefing.onlineBriefing.settings.competitor.brandName'),
        logo: [],
        color: this.defaultColor,
        dataSource: new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.folder),
    };
    competitors: MonitoringBrand[] = [];
    duplicateBrandIds: Record<number, boolean> = {};

    constructor(
        private briefingsService: BriefingsService,
        private coreDialogService: CoreDialogService,
        private monitoringRoutingService: MonitoringRoutingService,
        private briefingsTemplateFactorService: BriefingsTemplateFactorService,
        private briefingCoverageAnalysisService: BriefingCoverageAnalysisService,
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private mentionSearchSettingService: MentionSearchSettingService,
        private localizationService: LocalizationService,
        private monitoringBrandManagementService: MonitoringBrandManagementService,
        private briefingTemplateService: BriefingTemplateService,
        private messageService: MessageService,
        private topicsService: TopicsService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.updateDataSourceConfigs();
        this.updateMonthlyConfigs();
        this.updateQuartlyConfigs();
        this.subscriptions.push(
            // get past briefings
            this.briefingsService.getBriefingListItems().subscribe(
                list => {
                    if(list){
                        this.briefingListItems = list.filter(item => item.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE).slice().sort(
                            (a, b) => {
                                const aSortDate = a.modifiedDate ? +a.modifiedDate : +a.createdDate;
                                const bSortDate = b.modifiedDate ? +b.modifiedDate : +b.createdDate;
                                return bSortDate - aSortDate;
                            }
                        );
                    }
                }
            ),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.openAI]).subscribe(hasPermission => {
                this.hasOpenAI = hasPermission;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(x => {
                this.dateFormat = x.dateFormat;
                this.timezonePreference = x.timeZone;
                this.timeFormatPreference = x.timeFormat;
                this.hasAgent = this.currentUserStateService.agenticFeatureEnabled();
                this.hasAgentDebug = this.currentUserStateService.debugTopicAgenticWorkflowEnabled();
            }),
            this.briefingTemplateService.all$.subscribe(items => {
                this.canvasTemplateListItems = items
                    .filter(item => item.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) || [];
            })
        );
    }

    ngOnInit() {
        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(prefs => prefs.reportsPrefs.defaultChartColors.generalColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors))
            ).subscribe(colors => {
                this.defaultColors = [...Object.values(colors)];
            })
        );

        // Auto-submit Canvas Creation Agent if navigated from the briefings hype page with a prompt
        const agentPrompt = (history.state as { agentPrompt?: string })?.agentPrompt;
        if (agentPrompt && this.currentUserStateService.agenticFeatureEnabled()) {
            this.naturalLanguagePrompt = agentPrompt;
            // Clear the state so a page refresh does not re-trigger the agent
            history.replaceState({}, '');
            this.createBriefingFromPrompt();
        }
    }

    private updateDuplicateBrandIds(): void {
        this.duplicateBrandIds = this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR
            ? this.monitoringBrandManagementService.getDuplicateBrandIds(this.brandCard, this.competitors)
            : {};
    }

    selectTemplate(template: TemplateCardModel): void {
        this.onlineBriefingTemplates.forEach(t => t.selected = false);
        template.selected = true;
        this.selectedTemplate = template.id as OnlineBriefingTemplateEnum;
        if (this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            this.addCompetitor();
        }
        this.updateMonthlyConfigs();
        this.updateQuartlyConfigs();
    }

    /**
    * Responds to changes in the scheduling type
    */
    schedulingTypeChanged(newSchedulingType: deliverySchedulingTypesEnum): void {
        this.selectedSchedulingType = newSchedulingType;
        switch (newSchedulingType) {
            case deliverySchedulingTypesEnum.immediate:
                this.briefing.sendAt.setSendNow();
                this.mentionSearchSettingService.updateSelectType(MonitoringSeriesTypeEnum.folder);
                this.updateHasUnsavedSource();
                break;
            case deliverySchedulingTypesEnum.recurring:
                this.briefing.sendAt.setRecurring(new BriefingRecurrencePatternModel());
                this.mentionSearchSettingService.updateSelectType(MonitoringSeriesTypeEnum.topic);
                this.updateHasUnsavedSource();
                break;
        }
        this.updateMonthlyConfigs();
        this.updateQuartlyConfigs();
    }

    updateDataSourceConfigs() {
        this.brandCardDataSourceConfigs = {
            showResultsFeedback: !this.enableCoPilot,
            showResultsFeedbackAnalysis: this.enableCoPilot,
        };
        this.competitorCardDataSourceConfigs = {
            dataSourceLabel: 'Briefing.onlineBriefing.settings.competitor.source',
            showDateRange: false,
            showResultsFeedback: !this.enableCoPilot,
            showResultsFeedbackAnalysis: this.enableCoPilot,
        };
    }

    updateMonthlyConfigs() {
        this.monthlyConfigs = {
            monthlyDateRange: this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY,
            monthlySettings: this.monthlySettings,
            restrictMonthToMostRecent: this.selectedSchedulingType === deliverySchedulingTypesEnum.recurring
        } as MonthlyConfigs;
    }

    updateQuartlyConfigs() {
        this.quarterlyConfigs = {
            quarterlyDateRange: this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY,
            quarterlySettings: this.quarterlySettings,
            restrictQuarterToMostRecent: this.selectedSchedulingType === deliverySchedulingTypesEnum.recurring
        } as QuarterlyConfigs;
    }

    onBannerStyleSelect(bannerStyle: OnlineBriefingBannerStyleEnum): void {
        this.selectedBannerStyle = bannerStyle;
    }

    populateCompetitors() {
        if (this.selectedTemplate !== OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            return;
        };
        const io: ConfirmDialogModel = {
            inputs: {
                heading: this.localizationService.transform(
                    'Briefing.onlineBriefing.settings.brand.populateCompetitors.heading',
                    { name: this.brandCard.name }
                ),
                message: this.localizationService.transform(
                    'Briefing.onlineBriefing.settings.brand.populateCompetitors.message',
                    { name: this.brandCard.name }
                ),
                cancelLabel: 'Briefing.onlineBriefing.settings.brand.populateCompetitors.cancel',
                okLabel: 'Briefing.onlineBriefing.settings.brand.populateCompetitors.confirm',
                id: 'CanvasAddCompetitors'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.monitoringBrandManagementService.getBrandCompetitors(this.brandCard).pipe(
                            takeUntil(this.destroy$)
                        ).subscribe(competitors => {
                            this.competitors = competitors || [];
                            this.updateDuplicateBrandIds();
                            requestAnimationFrame(() => {
                                this.updateHasUnsavedSource();
                                this.rightScrollPanel?.scrollTo(100000, 0);
                            });
                        });
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    addCompetitor(): void {
        this.monitoringBrandManagementService.addCompetitor(
            undefined,
            this.competitors.length,
            [
                this.brandCard.color,
                ...this.competitors.map(competitor => competitor.color)
            ]
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe(competitor => {
            this.competitors.push(competitor);
            this.updateDuplicateBrandIds();
            requestAnimationFrame(() => {
                this.updateHasUnsavedSource();
                this.rightScrollPanel?.scrollTo(100000, 0);
            });

        });
    }

    deleteCompetitor(index: number): void {
        this.competitors.splice(index, 1);
        this.updateDuplicateBrandIds();
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
    }

    moveUp(index: number) {
        this.moveCompetitor(index, index - 1);
    }

    moveDown(index: number) {
        this.moveCompetitor(index, index + 1);
    }

    moveCompetitor(from: number, to: number) {
        const competitor = this.competitors[from];
        this.competitors.splice(from, 1);
        this.competitors.splice(to, 0, competitor);
    }

    onBrandCardChanged() {
        this.updateDuplicateBrandIds();
        this.monitoringBrandManagementService.syncCompetitorDateRangesToBrand(this.brandCard, this.competitors);
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
    }

    onCompetitorCardChanged(competitor: MonitoringBrand, index: number) {
        this.competitors[index] = competitor;
        this.updateDuplicateBrandIds();
        this.monitoringBrandManagementService.syncCompetitorDateRangesToBrand(this.brandCard, this.competitors);
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
    }

    updateHasUnsavedSource(): void {
        const hasUnsavedSource = [
            this.brandCard.dataSource,
            ...(this.competitors?.map(item => item.dataSource) ?? [])
        ].some(source => source?.isUnsavedSource());

        this.mentionSearchSettingService.updateHasUnsavedSourceType(hasUnsavedSource);
    }

    cancel(): void {
        this.monitoringRoutingService.gotoBriefings();
    }

    onBriefingSelect(briefing: BriefingListItemModel): void {
        this.selectedBriefing = briefing;
    }

    onCanvasTemplateSelect(template: BriefingListItemModel): void {
        this.selectedCanvasTemplate = template;
    }

    openManageTemplateDialog(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        this.coreDialogService.open(ManageTemplateDialogComponent, {
            inputs: {
                animationTrigger,
                overlayContainer
            }
        });
    }

    nextStep(): void {
        if (this.selectedTabIndex === NewOnlineBriefingTabsEnum.quickTemplates) {
            if (this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_BLANK) {
                this.briefing = this.briefingsTemplateFactorService.getBlankCanvas(this.dateFormat);
                this.briefingsService.setNewBriefing(this.briefing);
            } else {
                this.isChoosingTemplate = false;
            }
        } else if (this.selectedTabIndex === NewOnlineBriefingTabsEnum.savedTemplates) {
            if (this.selectedCanvasTemplate) {
                this.loadCanvasTemplate(this.selectedCanvasTemplate.id).subscribe();
            }
        } else {
            if (this.selectedBriefing) {
                this.briefingsService.cloneFromExistingBriefing(this.selectedBriefing.id);
            }
        }
    }

    // Rebuild combinedSearchCriteria for a data source revived from minimal JSON
    // (type + id only). getBatchOfCuratedMentions uses combinedSearchCriteria
    // directly, so without this the curated mentions API call returns nothing.
    private rebuildCombinedSearchCriteria(dataSource: MonitoringBackEndDataSourceModel): void {
        if (!dataSource?.id) {
            return;
        }
        const combined = dataSource.filterSearchCriteria?.clone() ?? new MentionSearchCriteriaModel();
        switch (dataSource.type) {
            case MonitoringSeriesTypeEnum.folder:
            case MonitoringSeriesTypeEnum.allSavedCoverage:
                combined.query.folder = { include: [dataSource.id], exclude: [] };
                break;
            case MonitoringSeriesTypeEnum.tag:
                combined.query.tag = { include: [dataSource.id], exclude: [] };
                break;
            case MonitoringSeriesTypeEnum.tagGroup:
                combined.query.tagGroup = { include: [dataSource.id], exclude: [] };
                break;
            case MonitoringSeriesTypeEnum.aiAgent:
                combined.query.aiAgent = { include: [dataSource.id], exclude: [] };
                break;
            case MonitoringSeriesTypeEnum.subscription:
                combined.query.subscription = { include: [dataSource.id], exclude: [] };
                break;
        }
        dataSource.combinedSearchCriteria = combined;
    }

    loadCanvasTemplate(id: number): Observable<BriefingModel> {
        return this.briefingTemplateService.getBriefingTemplate(id).pipe(
            tap((template) => {
                delete template.id;
                template.type = BriefingTypeEnum.BRIEFING;
                template.status = BriefingStatusEnum.DRAFT;

                if (
                    template.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE
                    && template.canvasPreview
                ) {
                    const options = template.canvasPreview;
                    const fresh = this.briefingsTemplateFactorService.getTemplate(options);
                    template.blocks = fresh.blocks;
                    template.generalStyles = fresh.generalStyles;

                    // reviveDataSource() only restores type/id/name — combinedSearchCriteria
                    // is left empty, so getBatchOfCuratedMentions() fetches nothing.
                    // Rebuild it from the known type and id so content loads properly.
                    this.rebuildCombinedSearchCriteria(options.dataSource);
                    options.competitors?.forEach(c => this.rebuildCombinedSearchCriteria(c.dataSource));

                    if (options.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
                        this.briefingsService.createCompetitorBriefing(template, options);
                    } else {
                        this.briefingsService.createMediaCoverageBriefing(template, options);
                    }
                } else {
                    this.briefingsService.setNewBriefing(template);
                }
            })
        );
    }

    createBriefingFromPrompt(): void {
        const progressState: CanvasProgressState = {
            steps: [
                {
                    id: 'analyzing_prompt',
                    labelKey: 'Briefings.newBriefing.naturalLanguage.loadingDialog.analyzingPrompt',
                    status: 'in_progress'
                },
                {
                    id: 'creating_topics',
                    labelKey: 'Briefings.newBriefing.naturalLanguage.loadingPanel.step.creatingTopics',
                    status: 'pending'
                },
                {
                    id: 'refining_topics',
                    labelKey: 'Briefings.newBriefing.naturalLanguage.loadingPanel.step.refiningTopics',
                    status: 'pending'
                }
            ]
        };
        const progressState$ = new BehaviorSubject<CanvasProgressState>(progressState);
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        // eslint-disable-next-line prefer-const
        let canvasSub: Subscription;
        const loadingDialogRef = this.coreDialogService.open(BriefingNaturalLanguageLoadingDialogComponent, {
            inputs: {
                progressState: progressState$,
                animationTrigger,
                overlayContainer: document.getElementById('main-content')
            },
            outputs: {
                dialogClose: () => {
                    canvasSub?.unsubscribe();
                    this.aiIsGenerating = false;
                }
            }
        });
        this.aiIsGenerating = true;
        canvasSub = this.briefingsService.canvasAgentV2(this.naturalLanguagePrompt).pipe(
            switchMap(({ runId }) =>
                this.briefingsService.getCanvasV2Events(runId).pipe(
                    timeout({ each: this.canvasGenerationIdleTimeoutMs }),
                    tap(event => this.handleCanvasV2ProgressEvent(event, progressState, progressState$)),
                    filter(event => event.type === CanvasV2EventTypeEnum.COMPLETE || event.type === CanvasV2EventTypeEnum.ERROR),
                    first()
                )
            ),
            switchMap(finalEvent => this.loadCanvasTopics(finalEvent).pipe(
                map(topics => ({ finalEvent, topics }))
            ))
        ).subscribe({
            next: ({ finalEvent, topics }) => {
                if (finalEvent.type === CanvasV2EventTypeEnum.ERROR) {
                    this.failCanvasGeneration(loadingDialogRef);
                    return;
                }

                const canvasResponse = finalEvent.data as unknown as NewCanvasAgentResponseApiModel;
                const sources = Object.values(canvasResponse.sources);

                // The agent reported success but couldn't persist every topic, so there is nothing
                // to point the briefing's data source at.
                if (sources.some(s => s.topicId === null)) {
                    this.failCanvasGeneration(
                        loadingDialogRef,
                        'Briefings.newBriefing.naturalLanguage.error.topicsNotCreated'
                    );
                    return;
                }

                this.brandCard.name = canvasResponse.briefingTitle || 'AI Generated Briefing';
                let recurrencePattern = null;
                let publishLaterDate: Date | null = null;
                // Set timing
                if (canvasResponse.schedulingType === CanvasAgentResponseApiModelSchedulingTypeEnum.PUBLISH_NOW) {
                    this.selectedSchedulingType = deliverySchedulingTypesEnum.immediate;
                } else if (canvasResponse.schedulingType === CanvasAgentResponseApiModelSchedulingTypeEnum.PUBLISH_LATER) {
                    // Online briefings only offer one-time and recurring scheduling; a deferred
                    // publish is a one-time briefing whose sendAt is set after the briefing is built.
                    this.selectedSchedulingType = deliverySchedulingTypesEnum.immediate;
                    if (canvasResponse.publishLaterTiming) {
                        // The agent returns a unix timestamp in seconds; sendAt stores milliseconds.
                        publishLaterDate = new Date(canvasResponse.publishLaterTiming * 1000);
                    }
                } else if (canvasResponse.schedulingType === CanvasAgentResponseApiModelSchedulingTypeEnum.RECURRING) {
                    this.selectedSchedulingType = deliverySchedulingTypesEnum.recurring;
                    recurrencePattern = new BriefingRecurrencePatternModel();
                    if (canvasResponse.recurrenceTiming.interval === CanvasAgentResponseApiModelRecurrenceTimingIntervalEnum.QUARTERLY) {
                        // Quarterly publishes the day after each quarter ends at noon (see QUARTERLY_MONTH_DAY_PAIRS);
                        // the agent sends no days or times for this cadence.
                        recurrencePattern.frequencyType = DeliveryRecurrenceFrequencyEnum.quarterly;
                        recurrencePattern.frequencyValues = [];
                        recurrencePattern.times = [new TimeModel(
                            timeFormatsEnum.twentyFourHour,
                            12 as TwentyFourHourModel,
                            0 as MinuteModel,
                            null,
                            this.timezonePreference
                        )];
                    } else {
                        if (canvasResponse.recurrenceTiming.interval === CanvasAgentResponseApiModelRecurrenceTimingIntervalEnum.DAILY) {
                            recurrencePattern.frequencyType = DeliveryRecurrenceFrequencyEnum.daily;
                            recurrencePattern.frequencyValues = [0, 1, 2, 3, 4, 5, 6];
                        } else if (canvasResponse.recurrenceTiming.interval === CanvasAgentResponseApiModelRecurrenceTimingIntervalEnum.WEEKLY) {
                            recurrencePattern.frequencyType = DeliveryRecurrenceFrequencyEnum.weekly;
                            recurrencePattern.frequencyValues = canvasResponse.recurrenceTiming.days.map(day => this.weekdayMap.indexOf(day));
                        } else {
                            recurrencePattern.frequencyType = DeliveryRecurrenceFrequencyEnum.monthly;
                            recurrencePattern.frequencyValues = canvasResponse.recurrenceTiming.days.map(day => (+day - 1));
                        }
                        recurrencePattern.times = canvasResponse.recurrenceTiming.sendTimes.map(timeStr => {
                            const time = new TimeModel();
                            const timeParts = timeStr.split(':');
                            if (this.timeFormatPreference === timeFormatsEnum.twelveHour) {
                                const hour = +timeParts[0];
                                if (hour > 12) {
                                    time.setMeridian(meridiansEnum.PM);
                                }
                                time.hour = (hour % 12 || 12) as TwentyFourHourModel;
                            } else {
                                time.hour = +timeParts[0] % 24 as TwentyFourHourModel;
                            }
                            time.setTimeZone(this.timezonePreference);
                            time.minute = +timeParts[1] % 60 as MinuteModel;
                            time.second = 0;
                            return time;
                        });
                    }
                }

                let dataSource: MonitoringBackEndDataSourceModel;
                switch (canvasResponse.templateId) {
                    case CanvasAgentTemplateEnum.MEDIA_COVERAGE_BRIEFING:
                        // from AI
                        this.analysisFocus = (canvasResponse.briefingAnalysisFocus || '').substring(0, this.analysisFocusMaxLength);
                        this.brandCard.color = canvasResponse.color || '#0D74BA';
                        this.selectedTemplate = OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_DEFAULT;

                        // hardcoded
                        this.enableCoPilot = true;
                        this.selectedBannerStyle = OnlineBriefingBannerStyleEnum.SIMPLE;

                        dataSource = this.createCanvasTopicDataSource(sources[0], topics);
                        this.applyAgentCoverageDateRange(dataSource, canvasResponse.coverageDateRange);
                        this.brandCard.dataSource = dataSource;

                        this.createBriefing();
                        this.aiIsGenerating = false;
                        break;
                    case CanvasAgentTemplateEnum.COMPARISON_BRIEFING:
                        // from AI
                        const mainTopic = sources[0];
                        this.brandCard.name = mainTopic.sourceName?.replace(/^AI topic - /i, '') || this.brandCard.name;
                        this.analysisFocus = (canvasResponse.briefingAnalysisFocus || '').substring(0, this.analysisFocusMaxLength);
                        this.brandCard.color = canvasResponse.color || '#0D74BA';
                        this.selectedTemplate = OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR;
                        const assignedColors = new Set<string>();
                        this.competitors = [];
                        sources.slice(1).forEach(source => {
                            const competitor = new MonitoringBrand();
                            competitor.name = source.sourceName;
                            const competitorDataSource = this.createCanvasTopicDataSource(source, topics);
                            this.applyAgentCoverageDateRange(competitorDataSource, canvasResponse.coverageDateRange);
                            competitor.dataSource = competitorDataSource;
                            const color = this.defaultColors.find(c => c !== this.brandCard.color && !assignedColors.has(c));
                            competitor.color = color;
                            assignedColors.add(color);
                            this.competitors.push(competitor);
                        });

                        // hardcoded
                        this.enableCoPilot = true;
                        this.selectedBannerStyle = OnlineBriefingBannerStyleEnum.SIMPLE;

                        dataSource = this.createCanvasTopicDataSource(sources[0], topics);
                        this.applyAgentCoverageDateRange(dataSource, canvasResponse.coverageDateRange);
                        this.brandCard.dataSource = dataSource;

                        this.createBriefing();
                        this.aiIsGenerating = false;
                        break;
                    case CanvasAgentTemplateEnum.QUARTERLY_COVERAGE_BRIEFING:
                        // from AI
                        this.analysisFocus = (canvasResponse.briefingAnalysisFocus || '').substring(0, this.analysisFocusMaxLength);
                        this.brandCard.color = canvasResponse.color || '#0D74BA';
                        this.selectedTemplate = OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY;
                        this.quarterlySettings = this.mapAgentQuarterlySettings(canvasResponse);

                        // hardcoded
                        this.enableCoPilot = true;
                        this.selectedBannerStyle = OnlineBriefingBannerStyleEnum.SIMPLE;

                        dataSource = this.createCanvasTopicDataSource(sources[0], topics);
                        // The manual flow applies the quarterly date range through the source picker;
                        // the AI flow bypasses the picker, so apply it to the data source here.
                        dataSource.filterSearchCriteria.query.dateQuery = this.getQuarterlyDateQuery();
                        dataSource.combinedSearchCriteria.query.dateQuery = dataSource.filterSearchCriteria.query.dateQuery;
                        this.brandCard.dataSource = dataSource;

                        this.createBriefing();
                        this.aiIsGenerating = false;
                        break;
                    case CanvasAgentTemplateEnum.MONTHLY_COVERAGE_BRIEFING:
                        // from AI
                        this.analysisFocus = (canvasResponse.briefingAnalysisFocus || '').substring(0, this.analysisFocusMaxLength);
                        this.brandCard.color = canvasResponse.color || '#0D74BA';
                        this.selectedTemplate = OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY;
                        this.monthlySettings = this.mapAgentMonthlySettings(canvasResponse);

                        // hardcoded
                        this.enableCoPilot = true;
                        this.selectedBannerStyle = OnlineBriefingBannerStyleEnum.SIMPLE;

                        dataSource = this.createCanvasTopicDataSource(sources[0], topics);
                        dataSource.dataSourceName = sources[0].sourceName?.replace(/^AI topic - /i, '');
                        // The manual flow applies the monthly date range through the source picker;
                        // the AI flow bypasses the picker, so apply it to the data source here.
                        dataSource.filterSearchCriteria.query.dateQuery = this.getMonthlyAgentDateQuery();
                        dataSource.combinedSearchCriteria.query.dateQuery = dataSource.filterSearchCriteria.query.dateQuery;
                        this.brandCard.dataSource = dataSource;

                        this.createBriefing();
                        this.aiIsGenerating = false;
                        break;
                    default:
                        this.failCanvasGeneration(loadingDialogRef);
                        return;
                }

                if (recurrencePattern) {
                    this.briefing.sendAt.setRecurring(recurrencePattern);
                } else if (publishLaterDate) {
                    this.briefing.sendAt.setSendLater(publishLaterDate);
                    this.briefing.sendAt.timeZone = this.timezonePreference;
                }
                this.coreDialogService.close(loadingDialogRef);
            },
            error: (err: unknown) => {
                this.failCanvasGeneration(
                    loadingDialogRef,
                    err instanceof TimeoutError ? 'Briefings.newBriefing.naturalLanguage.error.timedOut' : undefined
                );
            }
        });
    }

    /**
     * Loads the topics the agent created. The AI flow bypasses the source picker, which is what
     * normally copies a topic's query onto its data source; without it, blocks that search from the
     * data source (e.g. the curator) search all coverage instead of the topic.
     */
    private loadCanvasTopics(event: CanvasV2ProgressEvent): Observable<Map<number, MentionSearchModel>> {
        const sources = event.type === CanvasV2EventTypeEnum.COMPLETE
            ? Object.values((event.data as unknown as NewCanvasAgentResponseApiModel).sources)
            : [];
        const topicIds = sources.map(source => source.topicId).filter(id => !isNil(id));
        if (!topicIds.length) {
            return of(new Map());
        }
        return forkJoin(topicIds.map(id => this.topicsService.getTopic(id).pipe(take(1)))).pipe(
            map(topics => new Map(topics.map(topic => [topic.id, topic])))
        );
    }

    private createCanvasTopicDataSource(
        source: NewCanvasAgentSourceBlockModel,
        topics: Map<number, MentionSearchModel>
    ): MonitoringBackEndDataSourceModel {
        const dataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.topic, source.topicId);
        dataSource.dataSourceName = source.sourceName;
        dataSource.sourceSearchCriteria = topics.get(source.topicId)?.searchCriteria ?? dataSource.sourceSearchCriteria;
        return dataSource;
    }

    /**
     * Ends a failed Canvas generation: drops the progress dialog and tells the user, rather than
     * leaving them on the New Canvas page with no explanation.
     */
    private failCanvasGeneration(loadingDialogRef: string, message?: string): void {
        this.aiIsGenerating = false;
        this.coreDialogService.close(loadingDialogRef);
        this.messageService.add({
            severity: messageSeverityEnum.error,
            summary: 'Briefings.newBriefing.naturalLanguage.error.heading',
            detail: message ?? 'Briefings.newBriefing.naturalLanguage.error.message'
        });
    }

    private toolActivityLabel(toolName: string): string {
        return this.TOOL_ACTIVITY_LABELS[toolName] || 'Briefings.newBriefing.naturalLanguage.loadingPanel.topic.searchingInternet';
    }

    private handleCanvasV2ProgressEvent(
        event: CanvasV2ProgressEvent,
        state: CanvasProgressState,
        state$: BehaviorSubject<CanvasProgressState>
    ): void {
        const updateStep = (id: CanvasProgressStep['id'], updates: Partial<CanvasProgressStep>): void => {
            const step = state.steps.find(s => s.id === id);
            if (step) {
                Object.assign(step, updates);
            }
        };

        switch (event.type) {
            case CanvasV2EventTypeEnum.PLAN_COMPLETE: {
                const data = event.data as { topicCount?: number; topicKeys?: string[]; topicNames?: string[] };
                const topicCount = data?.topicCount ?? 0;
                const topics: CanvasProgressTopic[] = Array.from({ length: topicCount }, (_, i) => ({
                    key: data.topicKeys?.[i] || `placeholder_${i}`,
                    name: data.topicNames?.[i] || `Topic ${i + 1}`,
                    status: 'loading' as const
                }));
                updateStep('analyzing_prompt', { status: 'complete' });
                updateStep('creating_topics', { status: 'in_progress', topics });
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_SEARCH_TOOL_CALL: {
                const data = event.data as { key: string; toolName: string };
                const step = state.steps.find(s => s.id === 'creating_topics');
                const topic = step?.topics?.find(t => t.key === data.key);
                if (topic) {
                    topic.activity = this.toolActivityLabel(data.toolName);
                }
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_SEARCH_TOPIC_COMPLETE: {
                const data = event.data as { key: string; name?: string };
                const step = state.steps.find(s => s.id === 'creating_topics');
                const topic = step?.topics?.find(t => t.key === data.key) ?? step?.topics?.find(t => t.status === 'loading');
                if (topic) {
                    topic.key = data.key;
                    topic.name = data.name || data.key.replace(/_/g, ' ');
                    topic.status = 'complete';
                }
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_SEARCH_COMPLETE: {
                const step = state.steps.find(s => s.id === 'creating_topics');
                step?.topics?.forEach(t => {
                     if (t.status === 'loading') {
                        t.status = 'complete';
                    }
                });
                updateStep('creating_topics', { status: 'complete' });
                updateStep('refining_topics', { status: 'in_progress' });
                break;
            }
            case CanvasV2EventTypeEnum.EVALUATE_COMPLETE: {
                const data = event.data as { refinementCount?: number; refinementKeys?: string[]; refinementNames?: string[] };
                const refinementKeys = data?.refinementKeys ?? [];
                const topics: CanvasProgressTopic[] = refinementKeys.map((key, i) => ({
                    key,
                    name: data.refinementNames?.[i] || key.replace(/_/g, ' '),
                    status: 'loading' as const
                }));
                updateStep('refining_topics', { status: topics.length > 0 ? 'in_progress' : 'complete', topics });
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_REFINE_TOOL_CALL: {
                const data = event.data as { key: string; name?: string; toolName: string };
                const step = state.steps.find(s => s.id === 'refining_topics');
                if (step) {
                    let topic = step.topics?.find(t => t.key === data.key);
                    if (!topic) {
                        topic = { key: data.key, name: data.name || data.key.replace(/_/g, ' '), status: 'loading' };
                        step.topics = [...(step.topics ?? []), topic];
                    }
                    topic.activity = this.toolActivityLabel(data.toolName);
                }
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_REFINE_TOPIC_COMPLETE: {
                const data = event.data as { key: string; name?: string };
                const step = state.steps.find(s => s.id === 'refining_topics');
                if (step) {
                    let topic = step.topics?.find(t => t.key === data.key);
                    if (!topic) {
                        topic = { key: data.key, name: data.name || data.key.replace(/_/g, ' '), status: 'loading' };
                        step.topics = [...(step.topics ?? []), topic];
                    }
                    topic.name = data.name || topic.name;
                    topic.status = 'complete';
                }
                break;
            }
            case CanvasV2EventTypeEnum.TOPIC_REFINE_COMPLETE: {
                const step = state.steps.find(s => s.id === 'refining_topics');
                step?.topics?.forEach(t => {
                    if (t.status === 'loading') {
                        t.status = 'complete';
                    }
                });
                updateStep('refining_topics', { status: 'complete' });
                break;
            }
        }

        state$.next({ steps: state.steps.slice() });
    }


    createBriefing(): void {
        // Update UI: If there are errors, show error messages.
        this.allBrandCards?.toArray()?.forEach(card => card.validate());

        let isValid = true;

        isValid = isValid && !!this.brandCard.name?.trim() && !isNil(this.brandCard.dataSource?.id);

        if (this.selectedTemplate === this.onlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            isValid = this.competitors?.reduce((valid, competitor) => {
                return valid && !!competitor.name?.trim() && !isNil(competitor.dataSource?.id);
            }, isValid);
            isValid = isValid && Object.keys(this.duplicateBrandIds).length === 0;
        }

        if (!isValid) {
            this.scrollErrorsIntoView();
            return;
        }

        const onlineBriefingOption = {
            template: this.selectedTemplate,
            name: this.brandCard.name,
            brand: this.brandCard.brand,
            logo: this.brandCard.logo,
            accentColor: this.brandCard.color,
            dataSource: this.brandCard.dataSource,
            analysisFocus: this.analysisFocus,
            allowAi: this.enableCoPilot,
            bannerStyle: this.selectedBannerStyle,
            deliverySchedulingType: this.selectedSchedulingType,
            competitors: this.competitors,
            monthlySettings: this.monthlySettings,
            quarterlySettings: this.quarterlySettings
        } as OnlineBriefingTemplateConfigModel;

        this.briefing = this.briefingsTemplateFactorService.getTemplate(onlineBriefingOption);
        if (this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_DEFAULT) {
            this.briefingsService.createMediaCoverageBriefing(this.briefing, onlineBriefingOption);
        } else if (this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY) {
            this.createQuarterlyReportTemplate(onlineBriefingOption);
        } else if ( this.selectedTemplate === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY) {
            this.createMonthlyReportTemplate(onlineBriefingOption);
        } else {
            this.briefingsService.createCompetitorBriefing(this.briefing, onlineBriefingOption);
        }

        this.briefing.canvasPreview = new OnlineBriefingTemplateConfigModel(JSON.stringify(onlineBriefingOption));

        if (this.selectedSchedulingType === deliverySchedulingTypesEnum.immediate && this.enableCoPilot) {
            const aiAnalysisBlocks = [];
            this.briefing.blocks.forEach(block => {
                if (block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                    aiAnalysisBlocks.push(block);
                }

                if (block.isLayoutBlockType()) {
                    (block as LayoutBlock).blocks?.forEach(items => {
                        items?.forEach(item => {
                            if (item.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                                aiAnalysisBlocks.push(item);
                            }
                        });
                    });
                }
            });

            // Generate AI content for all analysis blocks except the curated coverage block as we'll need to load mentions for that separately
            this.briefingCoverageAnalysisService.generateAiContentForBlocks(aiAnalysisBlocks.filter(block =>
                (block.displayElement as AnalysisDisplayElementModel).backendDataSource.type !== MonitoringSeriesTypeEnum.allCuratedCoverageInBrief)
            );

            const curatorBlocks = this.briefing.blocks.filter(x => x.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR);
            curatorBlocks.forEach(curatorBlock =>
                this.briefingCoverageAnalysisService.curatorAnalyze(curatorBlock as CuratorBlock, true)
            );

            this.briefingCoverageAnalysisService.setAnalysisBlockWithAllCuratedCoverage(
                aiAnalysisBlocks.filter(block =>
                    (block.displayElement as AnalysisDisplayElementModel).backendDataSource.type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief)
            );
        }
    }

    private createMonthlyReportTemplate(onlineBriefingOption: OnlineBriefingTemplateConfigModel): void {
        const clonedDataSource = onlineBriefingOption.dataSource.clone();

        let monthlyDateQuery: DateQueryModel | undefined;
        if (onlineBriefingOption.monthlySettings?.isCalendarMonths) {
            monthlyDateQuery = this.buildMonthlyDateQuery(onlineBriefingOption.monthlySettings);
        } else {
            monthlyDateQuery = clonedDataSource.filterSearchCriteria?.query?.dateQuery;
        }

        if (!clonedDataSource.filterSearchCriteria) {
            clonedDataSource.filterSearchCriteria = new MentionSearchCriteriaModel();
        }

        if (monthlyDateQuery) {
            clonedDataSource.sourceSearchCriteria.query.dateQuery = monthlyDateQuery;
        }

        if (monthlyDateQuery && clonedDataSource.sourceSearchCriteria) {
            clonedDataSource.sourceSearchCriteria.query.dateQuery = monthlyDateQuery;
        }

        if (monthlyDateQuery && clonedDataSource.combinedSearchCriteria) {
            clonedDataSource.combinedSearchCriteria.query.dateQuery = monthlyDateQuery;
        }
        onlineBriefingOption.dataSource = clonedDataSource;

        this.briefing = this.briefingsTemplateFactorService.getTemplate(onlineBriefingOption);
        this.briefingsService.createMonthlyReportBriefing(this.briefing, onlineBriefingOption);
    }

    private buildMonthlyDateQuery(settings: MonthlyDateRangeSettingsModel | undefined): DateQueryModel {
        if (!settings?.selectedMonth || settings.selectedMonth === MonthOptionEnum.MOST_RECENT) {
            const query = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
            return query;
        }
        const monthIndex = parseInt(settings.selectedMonth.replace('M', ''), 10) - 1;
        const year = parseInt(settings.selectedYear ?? String(new Date().getFullYear()), 10);
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        dateQuery.absoluteRange = {
            start: moment().utc().year(year).month(monthIndex).date(1).startOf('day').toDate(),
            end: moment().utc().year(year).month(monthIndex).endOf('month').toDate()
        };
        return dateQuery;
    }

    private createQuarterlyReportTemplate(onlineBriefingOption: OnlineBriefingTemplateConfigModel): void {
        const clonedDataSource = onlineBriefingOption.dataSource.clone();
        // The quarterly date range is owned by the source picker, which applies it to filterSearchCriteria.
        const quarterlyDateQuery = clonedDataSource.filterSearchCriteria?.query?.dateQuery;
        if (quarterlyDateQuery && clonedDataSource.sourceSearchCriteria) {
            clonedDataSource.sourceSearchCriteria.query.dateQuery = quarterlyDateQuery;
        }
        onlineBriefingOption.dataSource = clonedDataSource;

        this.briefing = this.briefingsTemplateFactorService.getTemplate(onlineBriefingOption);
        this.briefingsService.createQuarterlyReportBriefing(this.briefing, onlineBriefingOption);
    }

    /**
     * Maps the canvas agent's quarterly settings onto the picker's model. Recurring quarterly
     * briefings always cover the most recently completed quarter, so the agent only returns an
     * explicit quarter for Publish Now / Publish Later.
     */
    private mapAgentQuarterlySettings(canvasResponse: NewCanvasAgentResponseApiModel): QuarterlyDateRangeSettingsModel {
        const settings = new QuarterlyDateRangeSettingsModel();
        const agentSettings = canvasResponse.quarterlySettings;
        if (canvasResponse.schedulingType === CanvasAgentResponseApiModelSchedulingTypeEnum.RECURRING || !agentSettings) {
            settings.selectedQuarter = QuarterOptionEnum.MOST_RECENT;
            return settings;
        }
        settings.isCalendarQuarters = agentSettings.isCalendarQuarters ?? true;
        settings.selectedQuarter = QuarterOptionEnum[agentSettings.selectedQuarter as keyof typeof QuarterOptionEnum] ?? QuarterOptionEnum.MOST_RECENT;
        settings.selectedYear = agentSettings.selectedYear ?? null;
        return settings;
    }

    /** Builds the date query the source picker would produce for the current quarterly settings. */
    /**
     * Applies the coverage window the agent read out of the prompt. Topic data sources default to
     * the last 24 hours, which would otherwise silently override the range the user asked for;
     * the quarterly and monthly templates apply their own range the same way.
     */
    private applyAgentCoverageDateRange(
        dataSource: MonitoringBackEndDataSourceModel,
        coverageDateRange: NewCanvasAgentCoverageDateRangeApiModel | null
    ): void {
        const dateQuery = coverageDateRange?.type === 'RELATIVE'
            ? this.buildAgentRelativeDateQuery(coverageDateRange.preset)
            : this.buildAgentAbsoluteDateQuery(coverageDateRange);
        if (!dateQuery) {
            return;
        }
        dataSource.filterSearchCriteria.query.dateQuery = dateQuery;
        dataSource.combinedSearchCriteria.query.dateQuery = dateQuery;
    }

    /**
     * A rolling window the agent picked ("the last 7 days"). The agent emits the same preset names
     * as RelativeTimezoneDateTypeEnum, but it is a separate service — an unrecognised preset is
     * dropped so the data source keeps its default rather than sending a value the backend rejects.
     */
    private buildAgentRelativeDateQuery(preset: string | null): DateQueryModel | null {
        const relativeType = RelativeTimezoneDateTypeEnum[preset as keyof typeof RelativeTimezoneDateTypeEnum];
        if (!relativeType) {
            return null;
        }
        const dateQuery = DateQueryModel.getRelativeTimezoneQuery(relativeType);
        dateQuery.timezone = this.timezonePreference;
        return dateQuery;
    }

    private buildAgentAbsoluteDateQuery(
        coverageDateRange: NewCanvasAgentCoverageDateRangeApiModel | null
    ): DateQueryModel | null {
        if (!coverageDateRange?.startDate || !coverageDateRange?.endDate) {
            return null;
        }
        const bounds = this.briefingsTemplateFactorService.getAgentCoverageDateBounds(
            coverageDateRange.startDate,
            coverageDateRange.endDate
        );
        if (!bounds) {
            return null;
        }
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        dateQuery.absoluteRange = { start: bounds[0], end: bounds[1] };
        return dateQuery;
    }

    private getQuarterlyDateQuery(): DateQueryModel {
        if (!this.quarterlySettings.selectedQuarter || this.quarterlySettings.selectedQuarter === QuarterOptionEnum.MOST_RECENT) {
            const query = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_QUARTER);
            query.timezone = this.timezonePreference;
            return query;
        }
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        const [start, end] = this.briefingsTemplateFactorService.getQuarterlyDateBounds(
            { quarterlySettings: this.quarterlySettings } as OnlineBriefingTemplateConfigModel
        );
        dateQuery.absoluteRange = { start, end };
        return dateQuery;
    }

    /**
     * Maps the canvas agent's monthly settings onto the picker's model. Recurring monthly
     * briefings always cover the most recently completed month, so the agent only returns an
     * explicit month for Publish Now / Publish Later.
     */
    private mapAgentMonthlySettings(canvasResponse: NewCanvasAgentResponseApiModel): MonthlyDateRangeSettingsModel {
        const settings = new MonthlyDateRangeSettingsModel();
        const agentSettings = canvasResponse.monthlySettings;
        if (canvasResponse.schedulingType === CanvasAgentResponseApiModelSchedulingTypeEnum.RECURRING || !agentSettings) {
            settings.selectedMonth = MonthOptionEnum.MOST_RECENT;
            return settings;
        }
        settings.isCalendarMonths = agentSettings.isCalendarMonths ?? true;
        settings.selectedMonth = MonthOptionEnum[agentSettings.selectedMonth as keyof typeof MonthOptionEnum] ?? MonthOptionEnum.MOST_RECENT;
        settings.selectedYear = agentSettings.selectedYear ?? null;
        return settings;
    }

    /** Builds the date query the source picker would produce for the current monthly settings. */
    private getMonthlyAgentDateQuery(): DateQueryModel {
        if (!this.monthlySettings.selectedMonth || this.monthlySettings.selectedMonth === MonthOptionEnum.MOST_RECENT) {
            const query = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
            query.timezone = this.timezonePreference;
            return query;
        }
        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        const [start, end] = this.briefingsTemplateFactorService.getMonthlyDateBounds(this.monthlySettings);
        dateQuery.absoluteRange = { start, end };
        return dateQuery;
    }

    scrollErrorsIntoView() {
        // Need an extra frame so angular has time to render the errors
        requestAnimationFrame(() => {
            const cards = this.allBrandCards?.toArray() ?? [];
            const refs = this.allBrandCardRefs?.toArray() ?? [];
            const brandCardPickerRef = this.brandCardDataSourceLeft ?? this.brandCardDataSourceRight;
            const errorRefs: ElementRef[] = [];

            cards.forEach((card, i) => {
                if (card.hasNameError) {
                    errorRefs.push(refs[i]);
                }
                if (card.hasDataSourceError) {
                    errorRefs.push(!card.showDataSourceField && brandCardPickerRef ? brandCardPickerRef : refs[i]);
                }
                // cards[0] is the main brandCardTemp; competitor cards follow in the same order as this.competitors.
                const competitorBrandId = this.competitors[i - 1]?.brand?.id;
                if (competitorBrandId !== null && competitorBrandId !== undefined && this.duplicateBrandIds[competitorBrandId]) {
                    errorRefs.push(refs[i]);
                }
            });

            this.rightScrollPanel?.scrollFirstIntoView(errorRefs);
            this.leftScrollPanel?.scrollFirstIntoView(errorRefs);
        });
    }

    onAgentBannerSubmit(prompt: string): void {
        this.naturalLanguagePrompt = prompt;
        this.openCanvasAgentDialog(prompt);
    }

    createBriefingFromPromptWithContext(): void {
        this.openCanvasAgentDialog(this.naturalLanguagePrompt);
    }

    private openCanvasAgentDialog(naturalLanguagePrompt: string): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(AIContextTakeoverComponent, {
            inputs: {
                title: 'Briefings.newBriefing.agilityAgent.contextTakeoverTitle',
                originalPurposeLabel: 'Briefings.newBriefing.prcopilot.originalPurpose',
                refinedPurposeLabel: 'Briefings.newBriefing.prcopilot.refinedPurpose',
                descriptionLabel: 'Briefings.newBriefing.prcopilot.descriptionLineOne',
                submitLabel: 'Briefings.newBriefing.prcopilot.updateCanvasPurpose',
                applyLabel: 'Briefings.newBriefing.prcopilot.create',
                naturalLanguagePrompt,
                taskDescription: this.taskDescription?.trim() || canvasAgentSystemPrompt,
                overlayContainer: document.getElementById('main-content'),
                animationTrigger
            },
            outputs: {
                dialogClose: (refinedPurpose: string) => {
                    if (refinedPurpose) {
                        this.naturalLanguagePrompt = refinedPurpose;
                        this.createBriefingFromPrompt();
                    }
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.destroy$.next();
        this.destroy$.complete();
    }
}
